// src/components/Policy/Policy.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import './Policy.css';

const Policy = () => {
  return (
    <div className="policy-page">
      {/* ===== HERO SECTION ===== */}
      <div className="policy-hero">
        <div className="policy-hero-overlay"></div>
        <div className="policy-hero-content">
          <span className="policy-hero-badge">ABVIG – AICIS</span>
          <h1>Event Requirements & Policies</h1>
          <p>
            Please review the following terms and conditions carefully. By registering for the AICIS 2026 Global Summit, 
            you agree to comply with all policies outlined below.
          </p>
        </div>
      </div>

      {/* ===== POLICY CONTENT ===== */}
      <div className="policy-container">
        <div className="policy-content-wrapper">

          {/* ===== SECTION 1: CANCELLATION & REFUND ===== */}
          <div className="policy-card">
            <div className="policy-card-header">
              <span className="policy-section-number">01</span>
              <h2>Conference Registration Cancellation & Refund Policy</h2>
            </div>
            <div className="policy-card-body">
              <p>
                The Africa Infrastructure, Climate Change, Security & Green Investment Summit (AICIS) is organized 
                by <strong>Africa Business Ventures & Investment Group (ABVIG)</strong>. By completing your registration 
                and making payment, you acknowledge that you have read, understood, and agreed to these terms and conditions.
              </p>

              <h4>1.1 Cancellation by Participant</h4>
              <p>
                Registered participants who are unable to attend the Summit may request cancellation subject to the 
                following conditions:
              </p>
              <ul>
                <li>Cancellation requests must be submitted in writing to the AICIS Secretariat at least <strong>30 calendar days</strong> before the official commencement of the Summit.</li>
                <li>Approved cancellations are eligible for a refund of up to <strong>60%</strong> of the registration fee, while <strong>40%</strong> shall be retained as an administrative and event processing fee.</li>
                <li>Requests received less than 30 days before the Summit are generally not eligible for a refund, except in exceptional circumstances approved by the Organising Committee.</li>
              </ul>

              <h4>1.2 Non-Refundable Charges</h4>
              <p>The following costs, where applicable, are non-refundable and will be deducted before any approved refund is processed:</p>
              <ul>
                <li>Bank transaction and transfer charges</li>
                <li>International remittance fees</li>
                <li>SWIFT/Telex charges</li>
                <li>Payment gateway processing fees</li>
                <li>Currency conversion charges</li>
                <li>VAT and applicable taxes</li>
                <li>Government regulatory deductions</li>
                <li>Service and administrative processing fees</li>
              </ul>

              <h4>1.3 Registration Costs</h4>
              <p>Participants acknowledge that registration fees are committed towards advance event preparations, including but not limited to:</p>
              <ul>
                <li>Venue reservation</li>
                <li>Conference administration</li>
                <li>Hotel and accommodation commitments (where applicable)</li>
                <li>Catering, breakfasts and networking sessions</li>
                <li>Conference kits, identification badges and branded materials</li>
                <li>Event technology and logistics</li>
                <li>Printed conference materials</li>
                <li>Security and protocol services</li>
                <li>Other operational and consumable conference expenses</li>
              </ul>
              <p className="policy-note">Accordingly, registration fees are not fully recoverable once these commitments have been made.</p>

              <h4>1.4 Eligible Grounds for Refund Consideration</h4>
              <p>Refund requests may be considered under exceptional circumstances, including:</p>
              <ul>
                <li>Official visa refusal by the embassy or consulate of the host country</li>
                <li>Government-imposed travel restrictions or travel bans</li>
                <li>Serious illness or medical emergency supported by appropriate documentation</li>
                <li>Death of an immediate family member</li>
                <li>Other exceptional circumstances considered reasonable by the Organising Committee</li>
              </ul>
              <p>Supporting evidence may be required before any refund is approved.</p>

              <h4>1.5 Refund Processing</h4>
              <p>Where a refund request has been approved:</p>
              <ul>
                <li>Refunds will be processed within <strong>thirty (30) working days</strong> after the conclusion of the Summit.</li>
                <li>Refunds shall, where practicable, be made using the original method of payment.</li>
                <li>Processing timelines may vary depending on banking systems, international payment regulations, currency conversion procedures, and applicable financial compliance requirements.</li>
                <li>Participants acknowledge that unforeseen delays may occur due to administrative or bureaucratic procedures involving third-party institutions.</li>
                <li>Where such delays arise, the AICIS Secretariat will make reasonable efforts to facilitate and monitor the refund process; however, ABVIG and the AICIS Organising Committee shall not be held liable for delays beyond their reasonable control.</li>
              </ul>

              <h4>1.6 Non-Refundable Circumstances</h4>
              <p>No refund shall be granted where:</p>
              <ul>
                <li>False information was provided during registration.</li>
                <li>The participant is refused entry due to incomplete travel documentation.</li>
                <li>The participant voluntarily decides not to attend.</li>
                <li>The participant breaches Summit rules or applicable laws.</li>
                <li>There is reasonable evidence that registration was made for purposes unrelated to legitimate participation in the Summit, including attempts to circumvent immigration or border control regulations.</li>
              </ul>

              <h4>1.7 Organiser's Right</h4>
              <p>
                The AICIS Organising Committee reserves the right to approve or decline any refund request based on the 
                documentation provided and the circumstances of each case. All decisions of the Organising Committee 
                shall be final.
              </p>
            </div>
          </div>

          {/* ===== SECTION 2: VISA & IMMIGRATION ===== */}
          <div className="policy-card">
            <div className="policy-card-header">
              <span className="policy-section-number">02</span>
              <h2>International Visa & Immigration Policy</h2>
            </div>
            <div className="policy-card-body">
              <p>
                AICIS welcomes participants from across Africa and around the world.
              </p>
              <p>
                Upon confirmation of registration and payment, eligible participants may receive:
              </p>
              <ul>
                <li>An official Letter of Invitation</li>
                <li>Confirmation of Registration</li>
                <li>Other supporting conference documentation where applicable</li>
              </ul>
              <p className="policy-warning">
                <strong>⚠️ Issuance of these documents does not guarantee the approval of any visa application.</strong>
              </p>
              <p>
                The decision to issue a visa rests solely with the immigration authorities, embassy or diplomatic mission 
                of the host country, including but not limited to the United States, Canada, the United Kingdom, European 
                Union Member States and other participating countries.
              </p>
              <p>
                <strong>ABVIG and the AICIS Secretariat:</strong>
              </p>
              <ul>
                <li>Do not influence visa decisions</li>
                <li>Cannot intervene in embassy procedures</li>
                <li>Cannot guarantee visa approval</li>
                <li>Shall not be liable for losses resulting from visa refusal, processing delays or immigration decisions</li>
              </ul>
              <p>
                <strong>Applicants are advised to:</strong>
              </p>
              <ul>
                <li>Apply well in advance</li>
                <li>Submit complete and accurate documentation</li>
                <li>Comply with all immigration requirements of the host country</li>
                <li>Consult the official visa website of the relevant government before making travel arrangements</li>
              </ul>
            </div>
          </div>

          {/* ===== SECTION 3: CONDUCT & COMPLIANCE ===== */}
          <div className="policy-card">
            <div className="policy-card-header">
              <span className="policy-section-number">03</span>
              <h2>Participant Conduct & Immigration Compliance Policy</h2>
            </div>
            <div className="policy-card-body">
              <p>
                All participants are expected to maintain the highest standards of professional conduct throughout the Summit.
              </p>
              <p>
                <strong>Participants agree to:</strong>
              </p>
              <ul>
                <li>Respect the laws and regulations of the host country</li>
                <li>Observe all immigration, customs and border control requirements</li>
                <li>Use the Summit invitation solely for legitimate conference participation</li>
                <li>Maintain appropriate professional behaviour during all Summit activities</li>
              </ul>
              <p>
                Participants are solely responsible for complying with the terms and conditions of their visa, immigration 
                status and authorised period of stay.
              </p>
              <p>
                Where required by the applicable immigration laws of the host country, participants are expected to depart 
                the country before the expiration of their authorised stay unless they have obtained a lawful extension or 
                change of immigration status from the competent authorities.
              </p>
              <p>
                <strong>ABVIG and AICIS shall not be responsible for:</strong>
              </p>
              <ul>
                <li>Immigration violations committed by participants</li>
                <li>Visa overstays</li>
                <li>Deportation proceedings</li>
                <li>Entry refusals</li>
                <li>Immigration penalties imposed by any government authority</li>
              </ul>
              <div className="policy-warning-box">
                <strong>⚠️ Warning:</strong> Any participant found to have used the Summit registration or invitation for 
                fraudulent, unlawful or immigration-related abuse may:
                <ul>
                  <li>Have their registration cancelled without refund</li>
                  <li>Be disqualified from future AICIS programmes and events</li>
                  <li>Be reported to the relevant authorities where required by law</li>
                </ul>
              </div>
            </div>
          </div>

          {/* ===== SECTION 4: EVENT MODIFICATION ===== */}
          <div className="policy-card">
            <div className="policy-card-header">
              <span className="policy-section-number">04</span>
              <h2>Event Modification, Cancellation & Force Majeure</h2>
            </div>
            <div className="policy-card-body">
              <p>
                The Organisers reserve the right to postpone, reschedule, relocate or cancel the Summit due to 
                circumstances beyond their reasonable control, including but not limited to:
              </p>
              <ul>
                <li>Natural disasters</li>
                <li>Public health emergencies or pandemics</li>
                <li>Civil unrest or security concerns</li>
                <li>Government directives</li>
                <li>International travel restrictions</li>
                <li>Force majeure events</li>
              </ul>
              <p>
                Where the Summit is postponed, registrations may be transferred to the new event date. Any refund 
                arrangements shall be determined by the Organising Committee based on the prevailing circumstances.
              </p>
            </div>
          </div>

          {/* ===== SECTION 5: PRIVACY ===== */}
          <div className="policy-card">
            <div className="policy-card-header">
              <span className="policy-section-number">05</span>
              <h2>Privacy & Data Protection</h2>
            </div>
            <div className="policy-card-body">
              <p>
                By registering for AICIS, participants consent to the collection, processing and use of their personal 
                information solely for conference administration, event communications, accreditation, security, logistics 
                and related purposes, in accordance with applicable data protection laws.
              </p>
            </div>
          </div>

          {/* ===== SECTION 6: PHOTOGRAPHY ===== */}
          <div className="policy-card">
            <div className="policy-card-header">
              <span className="policy-section-number">06</span>
              <h2>Photography & Media Consent</h2>
            </div>
            <div className="policy-card-body">
              <p>
                Participants acknowledge that photographs, video recordings and other media may be taken during the Summit.
              </p>
              <p>
                By attending, participants grant ABVIG and AICIS permission to use such images and recordings for:
              </p>
              <ul>
                <li>Event reporting</li>
                <li>Promotional materials</li>
                <li>Publications</li>
                <li>Websites</li>
                <li>Social media</li>
                <li>Documentary and archival purposes</li>
              </ul>
              <p>Without additional compensation, except where prohibited by applicable law.</p>
            </div>
          </div>

          {/* ===== SECTION 7: ACCEPTANCE ===== */}
          <div className="policy-card">
            <div className="policy-card-header">
              <span className="policy-section-number">07</span>
              <h2>Acceptance of Terms</h2>
            </div>
            <div className="policy-card-body">
              <p>
                Completion of registration and payment constitutes acceptance of these Event Requirements and Policies.
              </p>
              <p>
                Participants confirm that they have read, understood and agree to comply with all Summit rules, 
                procedures and applicable laws.
              </p>
            </div>
          </div>

          {/* ===== CONTACT FOOTER ===== */}
          <div className="policy-contact-footer">
            <h3>For enquiries regarding these policies, please contact:</h3>
            <div className="policy-contact-grid">
              <div className="policy-contact-item">
                <span className="contact-icon">📍</span>
                <div>
                  <h4>AICIS Secretariat</h4>
                  <p>Hampton Apartment, Flats F4,
2 Mungo Park Close, Off Onitsha Crescent
Asokoro, Abuja, FCT</p>
                </div>
              </div>
              <div className="policy-contact-item">
                <span className="contact-icon">📞</span>
                <div>
                  <h4>Nigeria</h4>
                  <p>+234 703 294 1519 <br></br> +234 814 263 8552</p>
                </div>
              </div>
              <div className="policy-contact-item">
                <span className="contact-icon">📞</span>
                <div>
                  <h4>USA</h4>
                  <p>+1 412 287 9069</p>
                </div>
              </div>
              <div className="policy-contact-item">
                <span className="contact-icon">✉️</span>
                <div>
                  <h4>Email</h4>
                  <p>
                    <a href="mailto:aicisnigeria@abvig.org">aicisnigeria@abvig.org</a>
                    <br />
                    <a href="mailto:aicis@abvig.com">aicis@abvig.com</a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ===== BACK BUTTON ===== */}
          <div className="policy-back">
            <Link to="/" className="back-button">← Back to Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Policy;